import { AddPhotoAlternateOutlined } from '@vindr/icons';
import { acceptTypeToRegex, fileToBase64, getExtensionFileName } from '@vindr/utils/converter';
import { getFileType, uuidv4 } from '@vindr/utils/helpers/common.helper';
import { Button } from 'antd';
import { ModalStaticFunctions } from 'antd/es/modal/confirm';
import { MutableRefObject, memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { AttachmentFileAcceptType } from '../../../AttachmentFile/AttachmentFile';
import { useCaptureManagerUpdater } from '../context';
import { CaptureManagerState } from '../context/type';

const AddPhotoIcon = ({
  importRef,
  modal,
}: {
  importRef: MutableRefObject<HTMLInputElement>;
  modal: Omit<ModalStaticFunctions, 'warn'>;
}) => {
  const { t } = useTranslation('Vindoc');
  const dispatch = useCaptureManagerUpdater();

  const importFiles = useCallback(async () => {
    const files = importRef.current.files;

    if (files.length === 0) return;

    async function actionImport() {
      const list = (await Promise.all(
        Array.from(files).map(async (file) => {
          const tag = getFileType(file.type);

          return {
            url: tag === 'img' ? await fileToBase64(file) : URL.createObjectURL(file),
            id: uuidv4(),
            tag,
            name: file.name,
            type: file.type,
            file,
          };
        })
      )) as CaptureManagerState['storeFiles'];

      dispatch({ type: 'IMPORT_ATTACHMENT', payload: list });
    }

    const regexAcceptType = acceptTypeToRegex(AttachmentFileAcceptType);

    if (Array.from(files).some((file) => !regexAcceptType.test(getExtensionFileName(file.name)))) {
      modal.confirm({
        title: t('Warning'),
        content: t('Some files are not supported. Do you want to continue?'),
        okText: t('Yes'),
        cancelText: t('No'),
        okCancel: true,
        onOk: async () => await actionImport(),
        zIndex: 10_000,
      });

      return;
    }

    await actionImport();
  }, [dispatch, modal, t]);

  return (
    <>
      <Button
        className="flex items-center text-3xl"
        onClick={() => importRef.current.click()}
        title={t('Import files')}
        icon={<AddPhotoAlternateOutlined />}
      >
        <span className="text-base">{t('Import')}</span>
      </Button>
      <input
        ref={importRef}
        type="file"
        multiple
        hidden
        onChange={importFiles}
        accept={AttachmentFileAcceptType}
      />
    </>
  );
};

export default memo(AddPhotoIcon);
